import type { Tier } from '../lib/api';
import type { OddsState } from '../hooks/useOdds';
import type { QuoteState } from '../hooks/useQuote';
import type { PayoutState } from './PayoutAddressField';
import PendingValue from './PendingValue';
import { miningModeLabel } from '../lib/miningModes';
import {
  formatCents,
  formatHashrate,
  formatOneInChance,
  formatProbabilityPercent,
  formatTermDays,
} from '../lib/format';

/**
 * The purchase summary -- where the selections become an order.
 *
 * Hierarchy is the point: the total dominates, the configuration lines support it, and
 * the metadata sits quietly underneath. Everything is not equally important, so
 * everything is not equally prominent.
 *
 * Every monetary figure is a server value, quoted for the exact configuration on screen
 * and formatted. Nothing is multiplied, divided, or totalled here -- a custom amount and
 * a preset reach this component by the same route, carrying the same kind of figure.
 *
 * Scope discipline: no tax, fees, discounts, shipping, or renewal pricing appear,
 * because none of those are part of this milestone.
 */

interface OrderSummaryProps {
  tier: Tier | null;
  hashrateTh: number;
  termDays: number;
  miningMode: string;
  currency: string;
  payoutState: PayoutState;
  oddsState: OddsState;
  /** The authoritative price, or the reason there isn't one. */
  quoteState: QuoteState;
  /**
   * The code the customer has applied, if any.
   *
   * Passed separately from the quote so the summary knows a code is in play even while
   * a new quote is in flight -- otherwise the code line would vanish and reappear on
   * every re-quote, and the card would change height twice per keystroke.
   */
  appliedCode?: string | null;
}

function Row({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="mm-summary-row">
      <dt className="mm-summary-label">{label}</dt>
      <dd className="mm-summary-value" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

function payoutLabel(state: PayoutState): string {
  switch (state.status) {
    case 'valid':
      return 'Confirmed';
    case 'checking':
      return 'Checking…';
    case 'invalid':
      return 'Not valid yet';
    default:
      return 'Not entered';
  }
}

function oddsLabel(state: OddsState): string {
  if (state.status === 'ready') {
    return [formatProbabilityPercent(state.odds.probability), formatOneInChance(state.odds.oneInChance)]
      .filter(Boolean)
      .join(' · ');
  }
  if (state.status === 'loading') return 'Calculating…';
  if (state.status === 'unavailable') return 'Unavailable';
  return '—';
}

export default function OrderSummary({
  tier,
  hashrateTh,
  termDays,
  miningMode,
  currency,
  payoutState,
  oddsState,
  quoteState,
  appliedCode = null,
}: OrderSummaryProps) {
  const quote = quoteState.status === 'ready' ? quoteState.quote : null;

  /*
   * A price is either current or on its way. Both render the same block; only the last
   * state -- no price at all -- renders something different, and that is not a state the
   * page passes through while somebody drags the slider.
   */
  const pricePending = quoteState.status === 'loading';

  /** The code as the server confirmed it, or null while that answer is in flight. */
  const appliedCodeLabel = quote?.discount?.applied ? quote.discount.code : null;

  /*
   * Show the line when a code is genuinely applied, and hold its slot while a quote for
   * an already-applied code is refreshing. A REJECTED code shows nothing here -- the
   * discount field states the rejection, and the summary must not imply a code is in
   * force when it is not.
   */
  const showCodeLine = Boolean(appliedCodeLabel) || (pricePending && Boolean(appliedCode));

  return (
    <section
      aria-labelledby="summary-heading"
      data-testid="order-summary"
      className="mm-rail-card"
    >
      <h2 id="summary-heading" className="mm-rail-heading">
        Your order
      </h2>

      {/* WHAT is being bought leads; what it COSTS follows immediately. A customer
          scanning this should recognise their configuration before they read a number. */}
      <p
        className="mm-numeric text-xl font-semibold tracking-tight"
        data-testid="summary-configuration"
      >
        {tier ? tier.displayHashrate : formatHashrate(hashrateTh)} for {formatTermDays(termDays)}
      </p>
      <p className="mm-help mt-0.5" data-testid="summary-mode">
        {miningModeLabel(miningMode)}
      </p>

      {/*
        THE PRICE BLOCK KEEPS ITS SHAPE WHILE A QUOTE IS IN FLIGHT.

        It used to collapse to a single "Pricing…" line on every re-quote -- four lines
        became one, so this card and everything below it jumped on each drag of the
        slider. The same paragraphs now render throughout, with `PendingValue` standing
        in for the figures.

        NO STALE MONEY. Nothing here remembers a previous price. A figure that no longer
        describes the configuration on screen is absent, not carried over -- and the
        order button is gated on a READY quote regardless, so nothing stale can be
        purchased.
      */}
      <div className="mt-4">
        {quote || pricePending ? (
          <>
            <p className="mm-total" data-testid="summary-total">
              {quote ? formatCents(quote.totalCents, currency) : <PendingValue width="7ch" />}
            </p>

            {/*
              THE CODE, NAMED AND NOTHING MORE.

              It used to read "Includes $45.90 off with LAUNCH10", which stated the
              discount amount a second time -- Total Saved below already carries the
              money, and inclusive of this code. Two figures for one benefit invite the
              reader to add them together.

              The slot is held while a quote refreshes so applying a code does not make
              the card change height twice.
            */}
            {showCodeLine && (
              <p className="mm-help mm-numeric mt-1" data-testid="summary-discount">
                Code:{' '}
                {appliedCodeLabel ?? <PendingValue width="6ch" />}
              </p>
            )}

            <p className="mm-help mm-numeric mt-1" data-testid="summary-effective">
              {quote ? (
                <>
                  {formatCents(quote.effective.centsPerDay, currency)} per day over{' '}
                  {formatTermDays(termDays)}
                </>
              ) : (
                <PendingValue width="17ch" />
              )}
            </p>

            {/*
              TOTAL SAVED -- ONE SUBTRACTION THE SERVER ALREADY PERFORMED.

              Both figures come from the quote: `totalSavedCents` is the reference price
              minus the total actually being charged, and the percentage is derived from
              those same two amounts. Nothing is composed from percentages here, and no
              denominator is reconstructed.

              THAT IS WHY IT CANNOT DISAGREE WITH THE PRICE ABOVE. Every discount --
              hashrate band, duration, and any applied code -- is already inside
              `totalCents`, so a code being applied or removed moves this line by exactly
              the amount it moves the total. Adding percentages together would be both
              wrong arithmetic and a figure that drifts from what is charged.

              Absent at the baseline configuration rather than showing "Saved $0.00".
            */}
            {/*
              ALWAYS RENDERED ONCE THERE IS A PRICE BLOCK, so the card cannot change
              height between a configuration that saves and the baseline that does not.
              The line reserves itself and simply says nothing at the baseline rather
              than claiming "Saved $0.00".
            */}
            <p
              className="mm-summary-saving mm-numeric mt-1.5 min-h-[1.25rem]"
              data-testid="summary-total-saved"
            >
              {!quote ? (
                <PendingValue width="14ch" />
              ) : quote.totalSavedCents > 0 ? (
                <>
                  Total Saved: {formatCents(quote.totalSavedCents, currency)} (
                  {Math.round(quote.totalSavedBasisPoints / 100)}%)
                </>
              ) : null}
            </p>

            {/* The pending state in words, for assistive technology. The placeholders
                above are decorative and hidden from it. */}
            {pricePending && (
              <span className="sr-only" role="status" data-testid="summary-price-loading">
                Pricing…
              </span>
            )}
          </>
        ) : (
          /* Not "no price exists" -- the server's own reason, whatever it was. */
          <p className="mm-note mm-note--pending" data-testid="summary-no-price">
            <span>
              {quoteState.status === 'unpriceable' || quoteState.status === 'error'
                ? quoteState.reason
                : 'Choose an amount to see a price.'}
            </span>
          </p>
        )}
      </div>

      <dl
        className="mt-5 divide-y border-t pt-1"
        style={{ borderColor: 'var(--mm-border)' }}
      >
        {/* Hashrate, duration and mode are stated in the heading above -- repeating them
            here was the duplication Sidney flagged. */}
        <Row label="Payout address" value={payoutLabel(payoutState)} testId="summary-payout" />
        <Row label="Odds" value={oddsLabel(oddsState)} testId="summary-odds" />
      </dl>
    </section>
  );
}
